import { Platform } from 'react-native';
import DeviceInfo from 'react-native-device-info';

import LS from '../../utils/localStorageUtils';
import { collectDeltaDNA, engageDeltaDNA } from '../api';

interface ClientDeviceData {
	platform: string;
	browserName: string;
	browserVersion: string;
	deviceName: string;
	operatingSystem: string;
	operatingSystemVersion: string;
	appVersion?: string;
	codePushVersion?: string;
}

class DeltaDNAService {
	private initialized = false;

	async initialize(userId?: string) {
		console.log('is initialized', this.initialized);
		if (this.initialized) return;
		console.log('userId????', userId);
		try {
			await this.clientDeviceEvent(userId);
			this.initialized = true;
			console.log('[DeltaDNA] ✅ Initialized');
		} catch (error) {
			console.error('[DeltaDNA] ❌ Init failed:', error);
		}
	}

	reset() {
		this.initialized = false;
		console.log('[DeltaDNA] Reset');
	}

	// ========== DEVICE DATA ==========

	private async getClientDeviceData(): Promise<ClientDeviceData> {
		const data: ClientDeviceData = {
			platform: 'WEB',
			browserName: 'N/A',
			browserVersion: 'N/A',
			deviceName: 'N/A',
			operatingSystem: Platform.OS,
			operatingSystemVersion: 'N/A',
		};

		try {
			if (Platform.OS === 'web') {
				data.platform = 'WEB';
				data.browserName = this.getBrowserName();
				data.browserVersion = this.getBrowserVersion();
				data.deviceName = 'Desktop';
				data.operatingSystem = navigator.platform;
			} else if (Platform.OS === 'android') {
				data.platform = 'ANDROID';
				data.deviceName = await DeviceInfo.getDeviceName();
				data.operatingSystem = 'Android';
				data.operatingSystemVersion = await DeviceInfo.getSystemVersion();
				data.appVersion = DeviceInfo.getVersion();
			} else if (Platform.OS === 'ios') {
				data.platform = 'IOS';
				data.deviceName = await DeviceInfo.getDeviceName();
				data.operatingSystem = 'iOS';
				data.operatingSystemVersion = await DeviceInfo.getSystemVersion();
				data.appVersion = DeviceInfo.getVersion();
			}
		} catch (error) {
			console.warn('[DeltaDNA] Error getting device data:', error);
		}

		return data;
	}

	private getBrowserName(): string {
		const userAgent = navigator.userAgent;
		if (userAgent.includes('Firefox')) return 'Firefox';
		if (userAgent.includes('Chrome')) return 'Chrome';
		if (userAgent.includes('Safari')) return 'Safari';
		if (userAgent.includes('Edge')) return 'Edge';
		return 'Unknown';
	}

	private getBrowserVersion(): string {
		const userAgent = navigator.userAgent;
		const match = userAgent.match(/(Firefox|Chrome|Safari|Edge)\/(\d+)/);
		return match ? match[2] : 'Unknown';
	}

	// ========== API CALLS ==========

	private async post(endpoint: string, data: any = {}) {
		try {
			const response = await collectDeltaDNA(endpoint, data);
			console.log(`[DeltaDNA] 📊 Event: ${endpoint}`, data);
			return response;
		} catch (error) {
			console.warn(`[DeltaDNA] Failed to send ${endpoint}:`, error);
			return null;
		}
	}

	private async get(endpoint: string): Promise<any> {
		try {
			const response = await engageDeltaDNA(endpoint);
			return response?.data || null;
		} catch (error) {
			console.warn(`[DeltaDNA] Failed to get ${endpoint}:`, error);
			return null;
		}
	}

	// ========== TRACKING EVENTS ==========

	async clientDeviceEvent(userId?: string) {
		const data = await this.getClientDeviceData();
		const userStr = LS.get('auth_user');
		const user = userStr ? JSON.parse(userStr) : null;

		const finalUserId = userId || user?.id || user?.numericId?.toString();

		if (!finalUserId) {
			console.error('[DeltaDNA] Cannot send clientDevice - no userId');
			return;
		}

		await this.post('client_device', {
			userId: finalUserId,
			platform: data.platform,
			browserName: data.browserName,
			browserVersion: data.browserVersion,
			deviceName: data.deviceName,
			operatingSystem: data.operatingSystem,
			operatingSystemVersion: data.operatingSystemVersion,
			appVersion: data.appVersion,
			userLevel: user?.level || 1,
			userXP: 0,
			chipBalance: user?.chips || 0,
			isVip: user?.isVip || false,
			userLanguage: 'n/a',
		});
	}

	async tutorialSkippedEvent() {
		await this.post('tutorial_skipped', {});
	}

	async tutorialCompletedEvent() {
		await this.post('tutorial_completed', {});
	}

	async openShopEvent(
		platform: string,
		userLanguage: string,
		userLevel: number,
		userXP: number,
		chipBalance: number,
		shopId: string,
		shopType: string,
		shopName: number,
	) {
		await this.post('shop_entered', {
			platform,
			userLanguage,
			userLevel,
			userXP,
			chipBalance,
			shopId,
			shopType,
			shopNameId: shopName,
		});
	}

	async playerPaymentEvent(
		platform: string,
		chipBalance: number,
		userLanguage: string,
		currency: string,
		amount: number,
		provider: string,
		transactionId: string,
		offerId: string,
		productName: string,
		offerValue: number,
		offerType: number,
	) {
		await this.post('transaction', {
			platform,
			chipBalance,
			userLanguage,
			currency,
			amount,
			provider,
			transactionId,
			offerId,
			productName,
			offerValue,
			offerType,
		});
	}

	async openMyProfileEvent() {
		await this.post('my_profile_opened', {});
	}

	async openPlayerProfileEvent(username: string) {
		await this.post('other_profile_opened', { username });
	}

	async achievementViewedEvent(achievementId: string, platform: string, userLanguage: string) {
		await this.post('achievement_viewed', { achievementId, platform, userLanguage });
	}

	// ========== ENGAGE DECISION POINTS ==========

	async getEngageData(
		type:
			| 'AppStarted'
			| 'TournamentLobbyOpened'
			| 'StoreOpened'
			| 'StoreOpenedLowChips'
			| 'PurchaseCompleted'
			| 'DailyBonus',
	): Promise<any> {
		return await this.get(`decision_point?value=${type}`);
	}

	async engageOnAppStarted(): Promise<any> {
		return await this.getEngageData('AppStarted');
	}

	async engageOnTournamentLobbyOpened(): Promise<any> {
		return await this.getEngageData('TournamentLobbyOpened');
	}

	async engageOnStoreOpened(): Promise<any> {
		return await this.getEngageData('StoreOpened');
	}

	async engageOnStoreOpenedLowChips(): Promise<any> {
		return await this.getEngageData('StoreOpenedLowChips');
	}

	async engageOnPurchaseCompleted(): Promise<any> {
		return await this.getEngageData('PurchaseCompleted');
	}

	async engageOnDailyBonus(): Promise<any> {
		return await this.getEngageData('DailyBonus');
	}
}

export const deltaDNAService = new DeltaDNAService();
